import React, {FC, useState} from "react"
import moment from "moment"

import {useTheme} from "@irati/ui/src/global/useTheme"
import {Card} from "@irati/ui/src/Layout"
import Flex from "@irati/ui/src/Flex"
import {Body, Label} from "@irati/ui/src/Typography"
import {TextArea} from "@irati/ui/src/FormItem"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import id from "@irati/utils/id"
import ErrorText from "@irati/ui/src/ErrorText"
import {MAX_CHAR_LENGTH} from "@irati-web/fields/poll/votes/validation/comment"
import Tabs from "@irati/ui/src/Tabs"
import ProfilePic from "@irati/ui/src/ProfilePicture"
import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import {User} from "@irati/types"
import adminTracking from "@irati-tracking/admin"
import {useAddPayrollOrderCommentMutation, useOrderAuditLog} from "@irati/payrollpay/hooks"
import Button from "@irati/ui/src/Button"

import {BlackCircle, CommentContainer, StyledBody} from "./styled"

interface Comment {
  comment: string
  partnerUser: User
  name: string
  createdAt: string
}

interface Props {
  orderId: string
  onSuccess: () => void
  comments?: Comment[]
}

const COMMENTS_TAB = "comments"
const ACTIVITY_TAB = "activity"

const Comments: FC<Props> = ({comments = [], orderId, onSuccess}, props) => {
  const [message, setMessage] = useState("")
  const [error, setError] = useState("")
  const theme = useTheme()
  const {formatMessage} = useIntl()
  const [addPayrollComment] = useAddPayrollOrderCommentMutation()
  const {data} = useOrderAuditLog({order_id: orderId})

  const handleChange = (value: string) => {
    setMessage(value)
  }

  const handleOnClick = () => {
    if (!message || message.length > 2000) {
      return
    }

    adminTracking.orderManagementAddComment(orderId, message)

    addPayrollComment({
      variables: {
        order_id: orderId,
        comment: message
      }
    })
      .then(() => {
        setMessage("")
        onSuccess()
      })
      .catch((err) => {
        setError("admin.components.orderManagement.comments.commentFailed")
      })
  }

  const handleTabClick = (updatedTab: string) => {
    if (updatedTab === COMMENTS_TAB) {
      adminTracking.orderManagementCommentsTab(orderId)
    } else {
      adminTracking.orderManagementActivityTab(orderId)
    }
  }

  return (
    <Card marginLeft={2} padding={[2, 2]}>
      <Tabs
        mountOnEnter
        unmountOnExit
        onSelect={handleTabClick}
        tabs={[
          {
            title: formatMessage({id: "admin.components.orderManagement.comments.tabs.title1"}),
            key: COMMENTS_TAB,
            component: (
              <Flex direction="column" marginTop={2}>
                <Body small>
                  <FormattedMessage id="admin.components.orderManagement.comments.tabs.subtitle1" />
                </Body>
                <TextArea
                  hideLabel
                  value={message}
                  onChange={handleChange}
                  maxLength={MAX_CHAR_LENGTH}
                  placeholder={formatMessage({id: "admin.components.orderManagement.comments.tabs.placeholder1"})}
                />
                {error && (
                  <ErrorText>
                    <FormattedMessage id={error} />
                  </ErrorText>
                )}
                <Button id={id("submit", props)} onClick={handleOnClick} full>
                  <FormattedMessage id="admin.components.orderManagement.comments.tabs.btn1" />
                </Button>

                {comments.length > 0 &&
                  comments.map((comment: Comment) => {
                    return (
                      <CommentContainer direction="column" marginTop={2}>
                        <StyledBody small marginBottom={1}>
                          {comment.comment}
                        </StyledBody>
                        <Flex>
                          {comment.partnerUser && <ProfilePic user={comment.partnerUser} small marginRight id={id("target", props)} />}
                          <Label color={theme.colors.greys.grey50}>
                            {comment.partnerUser ? firstnameLastname(comment.partnerUser) : comment.name}
                          </Label>
                          <BlackCircle color={theme.colors.greys.grey50}>&nbsp;&#9679;&nbsp;</BlackCircle>
                          <Label color={theme.colors.greys.grey50}>{moment(comment.createdAt).format("DD MMM YYYY")}</Label>
                        </Flex>
                      </CommentContainer>
                    )
                  })}
              </Flex>
            )
          },
          {
            title: formatMessage({id: "admin.components.orderManagement.comments.tabs.title2"}),
            key: ACTIVITY_TAB,
            component: (
              <>
                {data?.audit_logs.length > 0 &&
                  data?.audit_logs.map((log) => {
                    const currentLog = log.changed_fields[1] || log.changed_fields[0]
                    if (currentLog) {
                      return (
                        <Flex direction="column" marginTop={2}>
                          <Flex>
                            <Body small>{log.name}</Body>
                            <BlackCircle>&nbsp;&#9679;&nbsp;</BlackCircle>
                            <Body small>{log.timestamp.substring(0, log.timestamp.length - 8)}</Body>
                          </Flex>
                          <Label color={theme.colors.greys.grey50} marginBottom={1}>
                            <FormattedMessage
                              id="admin.components.orderManagement.comments.tabs.label2"
                              values={{
                                name: currentLog.field_name,
                                prevValue: currentLog.previous_value,
                                newValue: currentLog.new_value
                              }}
                            />
                          </Label>
                        </Flex>
                      )
                    }
                  })}
                {data?.audit_logs.length === 0 && (
                  <Body small color={theme.colors.greys.grey50} marginTop={2}>
                    <FormattedMessage id="admin.components.orderManagement.comments.tabs.body2" />
                  </Body>
                )}
              </>
            )
          }
        ]}
      />
    </Card>
  )
}

export default Comments
